// useUnternehmensverbund.ts — lädt das Umfeld eines Kontakts: bei einer Firma
// den Unternehmensverbund, bei einer Person den Haushalt. Die Kontaktsuche
// zeigt es für den eindeutigen Treffer (`trefferKandidat`) als Tabellenzeilen.

import { useEffect, useRef, useState } from "react";
import { apiFetch } from "../../../utils/apiFetch";
import type { TrefferKandidat, Unternehmensverbund } from "./unternehmensverbund";
import type { Haushalt } from "./haushalt";
import { haushaltPlan, umfeldIds, verbundPlan, type UmfeldPlan } from "./trefferUmfeld";

async function ladeUmfeld<T>(pfad: string, signal?: AbortSignal): Promise<T | null> {
  const { data } = await apiFetch<T>(pfad, { signal });
  return data ?? null;
}

export function fetchUnternehmensverbund(firmaId: number, signal?: AbortSignal) {
  return ladeUmfeld<Unternehmensverbund>(`contact-relation/unternehmensverbund/${firmaId}`, signal);
}

export function fetchHaushalt(personId: number, signal?: AbortSignal) {
  return ladeUmfeld<Haushalt>(`contact-relation/haushalt/${personId}`, signal);
}

/**
 * Das Umfeld des Kontakts, den die Suche eindeutig trifft, als Plan plus
 * Tabellenzeilen (User 28.09.2026: Verbund/Haushalt als Zeilen der Tabelle).
 * `kandidat = null` → nichts. Die Zeilen lädt `ladeZeilen` über die
 * gewöhnliche Kontaktliste (`filters.Ids`) — damit gelten Mandant und
 * Sichtbarkeit wie bei den Treffern.
 *
 * Beim Wechsel des Kontakts wird die alte Antwort SOFORT verworfen: sonst
 * stünde für einen Moment das Umfeld der vorigen Suche unter den neuen Treffern.
 * `neuLadenKey` lädt die Zeilen neu (z. B. geänderte include-Tokens).
 */
export function useTrefferUmfeld<R>(
  kandidat: TrefferKandidat | null,
  ladeZeilen: (ids: number[], signal: AbortSignal) => Promise<R[]>,
  neuLadenKey = "",
): { plan: UmfeldPlan | null; zeilen: R[] } {
  const [stand, setStand] = useState<{ plan: UmfeldPlan | null; zeilen: R[] }>({ plan: null, zeilen: [] });
  const ladeRef = useRef(ladeZeilen);
  ladeRef.current = ladeZeilen;
  const art = kandidat?.art ?? null;
  const id = kandidat?.kontaktId ?? null;
  useEffect(() => {
    setStand({ plan: null, zeilen: [] });
    if (id == null || art == null) return;
    const ctrl = new AbortController();
    (async () => {
      const plan =
        art === "firma"
          ? verbundPlan(await fetchUnternehmensverbund(id, ctrl.signal))
          : haushaltPlan(await fetchHaushalt(id, ctrl.signal));
      // Antwort einer inzwischen überholten Suche nicht zeigen.
      if (ctrl.signal.aborted || !plan || plan.startId !== id) return;
      const ids = umfeldIds(plan);
      const zeilen = ids.length ? await ladeRef.current(ids, ctrl.signal) : [];
      if (!ctrl.signal.aborted) setStand({ plan, zeilen });
    })()
      // Ein Fehler hier ist kein Grund, die Trefferliste zu stören — das
      // Umfeld ist eine Zugabe und bleibt dann einfach weg.
      .catch(() => {});
    return () => ctrl.abort();
  }, [art, id, neuLadenKey]);
  return stand;
}
